import { Accessor, Component, Index, JSX, Match, Switch } from 'solid-js';
import {
  ControlMenuItemType,
  SplitControlDropdown,
  TrbToolbar,
} from '@trimble/ui-components/solidjs';
import { MainToolbarButton } from './MainToolbarButton';

/**
 * One button in the mobile-mapping main horizontal toolbar.
 */
export interface MainToolbarItem {
  readonly type: 'button';
  readonly id: string;
  readonly iconName: string;
  readonly ariaLabel: string;
  readonly tooltip?: string;
  readonly text?: string;
  readonly disabled?: boolean;
  readonly pressed?: boolean;
  action(): void;
}

/** A visual vertical separator rendered between button groups. */
export interface MainToolbarDivider {
  readonly type: 'divider';
  readonly id: string;
}

/**
 * A split tool picker: the icon replays the last-used tool (or deactivates when
 * active), the caret opens the menu. Renders a `SplitControlDropdown`.
 */
export interface MainToolbarSplitDropdown {
  readonly type: 'split-dropdown';
  readonly storageKey: string;
  readonly multipleSelection?: boolean;
  readonly id: string;
  readonly anchorIcon: string;
  readonly tooltip?: string;
  readonly items: ControlMenuItemType[];
  readonly pressed?: boolean;
  readonly isAnchorActive?: boolean;
  onAnchorActiveClick?(): void;
}

export interface MainToolbarCustomItem {
  readonly type: 'custom';
  readonly id: string;
  readonly content: JSX.Element;
}

export type MainToolbarEntry =
  | MainToolbarItem
  | MainToolbarDivider
  | MainToolbarSplitDropdown
  | MainToolbarCustomItem;

export interface MainToolbarProps {
  readonly ariaLabel: string;
  readonly items: MainToolbarEntry[];
  readonly leftItems?: MainToolbarEntry[];
  readonly buttonSize?: 'small' | 'medium';
}

export const MainToolbar: Component<MainToolbarProps> = (props) => {
  const resolvedSize = (): 'small' | 'medium' => props.buttonSize ?? 'medium';

  const renderItem = (item: Accessor<MainToolbarEntry>): JSX.Element => (
    <Switch>
      <Match when={item().type === 'divider'}>
        <div
          data-testid={`main-toolbar-${item().id}`}
          class="h-6 w-px bg-gray-1 dark:bg-gray-9"
          aria-hidden="true"
        />
      </Match>
      <Match
        when={
          item().type === 'custom' ? (item() as MainToolbarCustomItem) : null
        }
      >
        {(custom: Accessor<MainToolbarCustomItem>) => (
          <div data-testid={`main-toolbar-${custom().id}`}>
            {custom().content}
          </div>
        )}
      </Match>
      <Match
        when={
          item().type === 'split-dropdown'
            ? (item() as MainToolbarSplitDropdown)
            : null
        }
      >
        {(dropdown: Accessor<MainToolbarSplitDropdown>) => (
          <div data-testid={`main-toolbar-${dropdown().id}`}>
            <SplitControlDropdown
              anchorIcon={dropdown().anchorIcon}
              items={dropdown().items}
              position="right"
              pressed={dropdown().pressed}
              tooltip={dropdown().tooltip}
              isAnchorActive={dropdown().isAnchorActive}
              onAnchorActiveClick={dropdown().onAnchorActiveClick}
              size={resolvedSize()}
              storageKey={dropdown().storageKey}
              multipleSelection={dropdown().multipleSelection}
            />
          </div>
        )}
      </Match>

      <Match
        when={item().type === 'button' ? (item() as MainToolbarItem) : null}
      >
        {(button: Accessor<MainToolbarItem>) => (
          <MainToolbarButton
            testId={`main-toolbar-${button().id}`}
            iconName={button().iconName}
            ariaLabel={button().ariaLabel}
            tooltip={button().tooltip}
            text={button().text}
            size={resolvedSize()}
            pressed={button().pressed}
            disabled={button().disabled}
            action={button().action}
          />
        )}
      </Match>
    </Switch>
  );

  return (
    <TrbToolbar
      orientation="horizontal"
      ariaLabel={props.ariaLabel}
      addSeparators={false}
      testId="main-toolbar"
      customClasses={`relative ${resolvedSize() === 'medium' ? 'h-12' : 'h-10'} !w-full !justify-between !px-2 !py-0 !rounded-none !bg-white dark:!bg-gray-10 !shadow-none border-b border-gray-1 dark:border-gray-9 z-toolbar text-base-content`}
    >
      <div class="flex items-center gap-2">
        <Index each={props.leftItems ?? []}>{renderItem}</Index>
      </div>
      <div class="flex items-center gap-2">
        <Index each={props.items}>{renderItem}</Index>
      </div>
    </TrbToolbar>
  );
};
